import React, { useState, useEffect } from 'react';
import { Container, Button, Spinner, Alert } from 'react-bootstrap';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Pagination from '../components/Pagination';

const ExchangeRecords = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchExchangeRecords();
  }, [currentPage]);

  const fetchExchangeRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/financial/transactions', {
        params: {
          type: 'EXCHANGE',
          page: currentPage,
          limit: itemsPerPage,
        },
      });

      if (response.data.success) {
        setTransactions(response.data.data.transactions);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (err) {
      setError(
        err.response?.data?.message || 'Failed to fetch exchange records',
      );
      console.error('Error fetching exchange records:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      COMPLETED: 'success',
      PENDING: 'warning',
      FAILED: 'danger',
    };
    return statusColors[status] || 'secondary';
  };

  return (
    <Container
      fluid
      style={{
        maxWidth: isMobile ? '100%' : '85%',
        padding: 'clamp(1rem, 3vw, 1.5rem)',
        backgroundColor: '#FFFFFF',
        minHeight: '100vh',
      }}
    >
      {/* Header */}
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <Button
          variant='link'
          className='text-dark p-0'
          onClick={() => navigate(-1)}
        >
          <FaArrowLeft size={20} />
        </Button>
        <h5 className='mb-0'>Exchange Records</h5>
        <div></div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className='text-center py-5'>
          <Spinner animation='border' variant='primary' />
          <p className='mt-2 text-muted'>Loading exchange records...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert variant='danger' className='mt-3'>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {!loading && !error && transactions.length === 0 && (
        <div className='text-center py-5'>
          <p className='text-muted'>No exchange records found</p>
        </div>
      )}

      {/* Records List */}
      {!loading && !error && transactions.length > 0 && (
        <>
          {transactions.map((record) => (
            <div
              key={record.id}
              className='bg-white rounded shadow-sm p-3 mb-2 border'
            >
              <div className='fw-bold'>
                {record.fromCurrency} → {record.toCurrency}
              </div>
              <div className='text-muted small'>Amount: {record.amount}</div>
              <div className='text-muted small'>Status: {record.status}</div>
              <div className='text-muted small'>
                Time: {formatDate(record.createdAt)}
              </div>
            </div>
          ))}

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </>
      )}
    </Container>
  );
};

export default ExchangeRecords;
